import React, { useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ConditionalWrapper from 'components/application/ConditionalWrapper';
import ErrorMessage from 'components/application/ErrorMessage';
import RadioField from 'components/application/RadioField';
import SidePanel from 'components/application/SidePanel';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrackedPersonnelContext } from 'components/contexts/TrackedPersonnelContext';
import FormFooter from 'components/sidepanels/FormFooter';
import AttachmentsField from 'components/training/attachments/components/AttachmentsField';
import TrainingFormFields from 'components/training/TrainingFormFields';

import { sendAnalytics } from 'components/helpers/analytics';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function TrainingRequirementsSidePanel({
  closeSidePanel,
  createTrainingRequirement,
  currentTraining,
  currentTrainingRequirement,
  domainCourse,
  includedAttachments,
  includedCoverImages,
  onTrainingCalendarClose,
  onTrainingDateChange,
  onTrainingInputChange,
  onTrainingRequirementInputChange,
  personnelId,
  removeErrorStyling,
  requestError,
  resetRequestError,
  sidePanelContext,
  sidePanelIsOpen,
  submitDisabled,
  updateTraining,
}) {
  const { user: currentUser } = useCurrentActor();

  const [scrolledAmount, setScrolledAmount] = useState(0);

  const trackedPersonnelContext = useTrackedPersonnelContext();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.TrainingRequirementsSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const currentTrainingAttachments = useMemo(
    () =>
      includedAttachments.filter((a) =>
        currentTraining.attachmentIds.includes(Number(a.id)),
      ),
    [includedAttachments, currentTraining.attachmentIds],
  );

  const handleCancel = () => {
    closeSidePanel();
    resetRequestError();
  };

  const onBackdropClick = () =>
    !['new', 'edit'].includes(sidePanelContext) && closeSidePanel();

  return (
    <SidePanel
      bodyContent={
        <div className='popup__body-form'>
          <ErrorMessage
            isFallback={requestError.isFallback}
            validationErrors={requestError.validationErrors}
            wrapperClassName='form-container'
          />
          <div className='form-container'>
            <label className='field__label tw-font-medium'>
              <span className='tw-mr-1'>{i18nT('isCourseRequired.label')}</span>
            </label>
            <div className='tw-mb-4'>
              <RadioField
                checked={currentTrainingRequirement.isCourseRequired}
                label={i18nTCommon('shared.required')}
                name='required_true'
                onChange={() => {
                  onTrainingRequirementInputChange({
                    target: { name: 'isCourseRequired', value: true },
                  });
                }}
                value={true}
              />
              <div className='-tw-mt-2 tw-px-7 tw-text-s tw-font-normal'>
                {i18nT('isCourseRequired.radioFields.required.description')}
              </div>
            </div>
            <div>
              <RadioField
                checked={!currentTrainingRequirement.isCourseRequired}
                label={i18nTCommon('shared.optional')}
                name='required_false'
                onChange={() => {
                  onTrainingRequirementInputChange({
                    target: { name: 'isCourseRequired', value: false },
                  });
                }}
                value={false}
              />
              <div className='-tw-mt-2 tw-px-7 tw-text-s tw-font-normal'>
                {i18nT('isCourseRequired.radioFields.optional.description')}
              </div>
            </div>
            <hr className='-tw-mx-5 tw-my-8' />
            <label className='field__label tw-font-medium'>
              {i18nT('isRecordingTraining.label')}
            </label>
            <RadioField
              checked={!currentTrainingRequirement.isRecordingTraining}
              label={i18nTCommon('shared.no')}
              name='record_training_false'
              onChange={() => {
                onTrainingRequirementInputChange({
                  target: { name: 'isRecordingTraining', value: false },
                });
              }}
              value={false}
            />
            <ConditionalWrapper
              condition={
                trackedPersonnelContext.hasReachedLimit &&
                !trackedPersonnelContext.isCurrentPersonnelTracked
              }
              wrapper={(children) => (
                <Tooltip
                  className='tooltip-dark--max-w-xxs'
                  placement='left'
                  tooltip={i18nT('isRecordingTraining.disabledTooltip')}
                  trigger='hover'
                >
                  {children}
                </Tooltip>
              )}
            >
              <RadioField
                checked={currentTrainingRequirement.isRecordingTraining}
                disabled={
                  trackedPersonnelContext.hasReachedLimit &&
                  !trackedPersonnelContext.isCurrentPersonnelTracked
                }
                label={i18nTCommon('shared.yes')}
                name='record_training_true'
                onChange={() => {
                  onTrainingRequirementInputChange({
                    target: { name: 'isRecordingTraining', value: true },
                  });
                }}
                value={true}
              />
            </ConditionalWrapper>
            {currentTrainingRequirement.isRecordingTraining && (
              <>
                <TrainingFormFields
                  currentTraining={currentTraining}
                  domainCourse={domainCourse}
                  onInputChange={onTrainingInputChange}
                  onTrainingCalendarClose={onTrainingCalendarClose}
                  onTrainingDateChange={onTrainingDateChange}
                  removeErrorStyling={removeErrorStyling}
                  requestError={requestError}
                />
                <AttachmentsField
                  isVisible={sidePanelIsOpen}
                  label={i18nTCommon('shared.uploadEvidence')}
                  maxUploads={25}
                  name='attachmentIds'
                  onChange={onTrainingInputChange}
                  onUploadSuccess={() =>
                    sendAnalytics('Training evidence uploaded', {
                      currentUser,
                      personnelId,
                    })
                  }
                  seedAttachments={{
                    data: currentTrainingAttachments,
                    included: includedCoverImages,
                  }}
                  tooltip={i18nTCommon('shared.egCertificatesOrCards')}
                  value={currentTraining.attachmentIds}
                />
              </>
            )}
          </div>
        </div>
      }
      closeCallback={closeSidePanel}
      color={sidePanelContext === 'show' ? 'secondary' : 'tertiary'}
      contentContext={sidePanelContext}
      displayClose={false}
      footerContent={
        <FormFooter
          onCancel={handleCancel}
          onSubmit={
            sidePanelContext === 'new' ?
              createTrainingRequirement
            : updateTraining
          }
          submitButtonDisabled={submitDisabled}
          text={
            sidePanelContext === 'new' ?
              i18nTCommon('training.addTrainingCourse')
            : i18nTCommon('shared.saveChanges')
          }
        />
      }
      headerContent={
        <div className='popup__title popup__title--tertiary-split'>
          <h1 className='tw-mb-2 tw-text-s tw-font-medium tw-tracking-tight'>
            {i18nT(`title.${sidePanelContext === 'new' ? 'new' : 'edit'}`)}
          </h1>
          <h2 className='truncated-text-container--wrapped truncated-text-container--five-lines tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
            {domainCourse?.attributes?.name}
          </h2>
        </div>
      }
      isOpen={sidePanelIsOpen}
      onBackdropClick={onBackdropClick}
      scrolledAmount={scrolledAmount}
      setScrolledAmount={setScrolledAmount}
      submitDisabled={submitDisabled}
    />
  );
}

TrainingRequirementsSidePanel.propTypes = {
  domainCourse: resourceShape('course'),
  currentTraining: PropTypes.object.isRequired,
  currentTrainingRequirement: PropTypes.object.isRequired,
  includedAttachments: PropTypes.array.isRequired,
  sidePanelIsOpen: PropTypes.bool.isRequired,
  sidePanelContext: PropTypes.string.isRequired,
  requestError: PropTypes.object.isRequired,
  submitDisabled: PropTypes.bool.isRequired,
  personnelId: PropTypes.string,
  includedCoverImages: PropTypes.array.isRequired,
  closeSidePanel: PropTypes.func.isRequired,
  resetRequestError: PropTypes.func.isRequired,
  onTrainingDateChange: PropTypes.func.isRequired,
  onTrainingInputChange: PropTypes.func.isRequired,
  onTrainingRequirementInputChange: PropTypes.func.isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
  onTrainingCalendarClose: PropTypes.func.isRequired,
  createTrainingRequirement: PropTypes.func.isRequired,
  updateTraining: PropTypes.func,
};
